import { categoryColors } from "@plasma/data/categories";
import { type DayOfTheWeek, daysOfTheWeek } from "@plasma/data/time";
import { timeConvert24hrTo12hr } from "@plasma/utils/time-convert";
import { Link, useNavigate } from "@tanstack/react-router";
import { Clock, MoreVertical } from "lucide-react";
import { twJoin } from "tailwind-merge";

import type { CategoryListItem } from "~/api-refs";
import { enumIncludes } from "~/lib/parse-enum-value";

import { Badge } from "../../ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { categoryCardColors } from "../category-card-colors";

const isCategoryColor = enumIncludes(categoryColors);

export const CategoryCard = ({
  menuId,
  category,
}: {
  menuId: string;
  category: CategoryListItem;
}) => {
  return (
    <div className="relative">
      <div className="dropdown-menu">
        <CategoryCardDropdownMenu
          categoryId={category.categoryId}
          menuId={menuId}
        />
      </div>
      <Link
        className={twJoin(
          "link relative block h-40 w-full rounded-md border border-muted-foreground/5 bg-background px-5 py-4 shadow-xs transition-[color,box-shadow] hover:border-border focus-visible:outline-hidden focus-visible:ring-md focus-visible:ring-ring/50",
          categoryCardColors[
            isCategoryColor(category.color) ? category.color : "white"
          ]
        )}
        key={category.categoryId}
        params={{ menuId, categoryId: category.categoryId }}
        to="/menus/$menuId/categories/$categoryId"
      >
        <p className="title text-foreground">{category.name}</p>
        <div className="caption text-muted-foreground flex gap-1.5 pt-2">
          <Clock className="size-3.5" />
          <div className="whitespace-pre-wrap">
            {category.matchBusinessHours
              ? "All day"
              : formatAvailabilityTimes(category.availabilityTimes)}
          </div>
        </div>
        {category.isShared ? (
          <Badge className="absolute right-3 bottom-3" variant="warning">
            Shared
          </Badge>
        ) : null}
      </Link>
    </div>
  );
};

const formatAvailabilityTimes = (
  availabilityTimes: CategoryListItem["availabilityTimes"]
) => {
  if (!availabilityTimes) {
    return "";
  }
  const availability: [DayOfTheWeek, string | null][] = daysOfTheWeek.map(
    (day) => {
      const dayAvailability = availabilityTimes.filter(
        (availabilityTime) => availabilityTime.dayOfTheWeek === day
      );
      if (!dayAvailability.length) {
        return [day, null];
      }
      return [
        day,
        dayAvailability
          .toSorted((a, b) => a.startTime.localeCompare(b.startTime))
          .map(
            (availabilityTime) =>
              `${timeConvert24hrTo12hr(availabilityTime.startTime)} - ${timeConvert24hrTo12hr(availabilityTime.endTime)}`
          )
          .join(", "),
      ];
    }
  );
  const returnArray: [string[], string | null][] = [];
  for (const [day, times] of availability) {
    const lastElement = returnArray.at(-1);
    if (lastElement && lastElement[1] === times) {
      lastElement[0].push(day);
    } else {
      returnArray.push([[day], times]);
    }
  }
  return returnArray
    .filter((d) => d[1])
    .map(([days, time]) => {
      const timeStr = time ?? "";
      if (days.length > 2) {
        return `${days[0]?.slice(0, 3)}-${days.at(-1)?.slice(0, 3)}: ${timeStr}`;
      }
      if (days.length === 2) {
        return `${days[0]?.slice(0, 3)}, ${days[1]?.slice(0, 3)}: ${timeStr}`;
      }
      return `${days[0]?.slice(0, 3)}: ${timeStr}`;
    })
    .join("\n");
};

const CategoryCardDropdownMenu = ({
  menuId,
  categoryId,
}: {
  menuId: string;
  categoryId: string;
}) => {
  const navigate = useNavigate();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="absolute top-2 right-2 z-10 p-2" type="button">
          <MoreVertical className="size-5" />
          <span className="sr-only">Edit or remove this category</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {/* V4.1: Route-based modals for URL context tracking */}
        <DropdownMenuItem
          onClick={() =>
            navigate({ to: `/menus/${menuId}/categories/${categoryId}/edit` })
          }
        >
          Edit Category Info
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-destructive focus:bg-destructive/10 focus:text-destructive"
          onClick={() =>
            navigate({ to: `/menus/${menuId}/categories/${categoryId}/delete` })
          }
        >
          Remove Category
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
